import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  applyNodeChanges,
  Background,
  Controls,
  MiniMap,
  ReactFlow,
  ReactFlowProvider,
  useReactFlow,
  type Edge,
  type EdgeTypes,
  type Node,
  type NodeChange,
  type NodeTypes,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { useRetrievalExperience } from '../../../hooks/useRetrievalExperience';
import type { RetrievalGraphEdgeData, RetrievalGraphNodeData } from '../../../types/retrieval';
import EntityNode from './EntityNode';
import RelationshipEdge from './RelationshipEdge';

const nodeTypes: NodeTypes = {
  entity: EntityNode,
};

const edgeTypes: EdgeTypes = {
  relationship: RelationshipEdge,
};

const GraphExplorerInner: React.FC = () => {
  const { state, selectNode } = useRetrievalExperience();
  const { fitView } = useReactFlow();

  const nodes = useMemo<Node<RetrievalGraphNodeData>[]>(() => {
    const selected = state.selectedNodeId;
    const connected = new Set<string>();
    if (selected) {
      connected.add(selected);
      state.graphEdges.forEach((edge) => {
        if (edge.source === selected) {
          connected.add(edge.target);
        }
        if (edge.target === selected) {
          connected.add(edge.source);
        }
      });
    }

    return state.graphNodes.map((node) => ({
      id: node.id,
      type: 'entity',
      position: node.position,
      data: node.data,
      selected: selected === node.id,
      selectable: true,
      style:
        selected && !connected.has(node.id)
          ? { opacity: 0.25, transition: 'opacity 150ms ease' }
          : { transition: 'opacity 150ms ease' },
    }));
  }, [state.graphEdges, state.graphNodes, state.selectedNodeId]);

  const [displayNodes, setDisplayNodes] = useState<Node<RetrievalGraphNodeData>[]>(nodes);

  useEffect(() => {
    setDisplayNodes((prev) => {
      const prevPositions = new Map(prev.map((node) => [node.id, node.position]));
      return nodes.map((node) => ({
        ...node,
        position: prevPositions.get(node.id) || node.position,
      }));
    });
  }, [nodes]);

  const edges = useMemo<Edge<RetrievalGraphEdgeData>[]>(() => {
    const selected = state.selectedNodeId;
    return state.graphEdges.map((edge) => {
      const connected = selected ? edge.source === selected || edge.target === selected : false;
      return {
        id: edge.id,
        type: 'relationship',
        source: edge.source,
        target: edge.target,
        data: edge.data,
        animated: false,
        style: {
          opacity: selected ? (connected ? 1 : 0.2) : 0.7,
          strokeWidth: connected ? 2 : 1,
          transition: 'opacity 150ms ease, stroke-width 150ms ease',
        },
      };
    });
  }, [state.graphEdges, state.selectedNodeId]);

  useEffect(() => {
    if (displayNodes.length === 0) {
      return;
    }
    const timer = window.setTimeout(() => {
      fitView({ padding: 0.2, duration: 300, maxZoom: 1.2 });
    }, 60);
    return () => window.clearTimeout(timer);
  }, [fitView, displayNodes.length]);

  const handleNodesChange = useCallback((changes: NodeChange[]) => {
    setDisplayNodes((currentNodes) =>
      applyNodeChanges<Node<RetrievalGraphNodeData>>(
        changes as NodeChange<Node<RetrievalGraphNodeData>>[],
        currentNodes,
      ),
    );
  }, []);

  if (displayNodes.length === 0) {
    return (
      <div className="flex h-full min-h-[220px] items-center justify-center px-4 text-center text-sm text-neutral-500">
        Graph entities will appear here as retrieval traverses related projects and sources.
      </div>
    );
  }

  return (
    <ReactFlow
      nodes={displayNodes}
      edges={edges}
      nodeTypes={nodeTypes}
      edgeTypes={edgeTypes}
      onNodesChange={handleNodesChange}
      onNodeClick={(_event, node: Node) => {
        selectNode(node.id);
        window.dispatchEvent(
          new CustomEvent('curious:insights-open', { detail: { tab: 'context' } }),
        );
      }}
      onPaneClick={() => selectNode(null)}
      fitView
      minZoom={0.1}
      maxZoom={2.2}
      defaultEdgeOptions={{ type: 'relationship' }}
      proOptions={{ hideAttribution: true }}
    >
      <Background gap={20} size={1} color="#d4d4d8" />
      <Controls showInteractive={false} position="top-right" />
      <MiniMap
        pannable
        zoomable
        className="!rounded-lg !border !border-neutral-200 !bg-white"
        nodeStrokeWidth={2}
      />
    </ReactFlow>
  );
};

const GraphExplorer: React.FC = () => {
  return (
    <div className="h-full min-h-[240px] w-full">
      <ReactFlowProvider>
        <GraphExplorerInner />
      </ReactFlowProvider>
    </div>
  );
};

export default GraphExplorer;
